import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Target,
  Search,
  Ruler,
  Hammer,
  Key,
  Building2,
  Users,
  TrendingDown,
  TrendingUp,
  LayoutGrid,
  ArrowUpRight,
  Star,
  ArrowRight,
  ChevronRight,
  Phone,
  Bath,
  BedDouble,
  Maximize2,
  Calendar,
} from "lucide-react";
import {
  companyInfo,
  stats,
  services,
  whyMarseille,
  realisations,
  testimonials,
  heroImages,
  marseilleImage,
} from "../data/mockData";
import { articles } from "../data/articles";

const iconMap = {
  Target,
  Search,
  Ruler,
  Hammer,
  Key,
  Building2,
  Users,
  TrendingDown,
  TrendingUp,
  LayoutGrid,
  ArrowUpRight,
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const CountUp = ({ end, suffix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const numericEnd = parseInt(end);

  return (
    <span ref={ref}>
      {isInView ? (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {end}
          {suffix}
        </motion.span>
      ) : (
        "0"
      )}
    </span>
  );
};

const HomePage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-[#FAF9F6]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-[600px] h-[600px] rounded-full bg-[#C4956A]/5 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#E8DDD3]/30 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C4956A]/10 rounded-full">
                <div className="w-2 h-2 rounded-full bg-[#C4956A] animate-pulse" />
                <span className="text-sm font-medium text-[#C4956A]">
                  Investissement locatif clé en main
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-[#1A1A2E] leading-tight">
                Investir en immobilier,{" "}
                <span className="text-[#C4956A]">sans charge mentale</span>
              </h1>
              <p className="text-lg text-[#1A1A2E]/60 leading-relaxed max-w-lg">
                De la recherche du bien à la mise en location, Key Vest Massilia
                pilote votre projet d'investissement à Marseille de A à Z.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#C4956A] text-white font-medium rounded-lg hover:bg-[#B38459] transition-all duration-300 hover:shadow-xl hover:shadow-[#C4956A]/20"
                >
                  Prendre rendez-vous
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/solution"
                  className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#1A1A2E]/15 text-[#1A1A2E] font-medium rounded-lg hover:border-[#C4956A]/30 hover:bg-[#C4956A]/5 transition-all duration-300"
                >
                  Découvrir notre solution
                </Link>
              </div>
              {/* Stats row */}
              <div className="flex items-center gap-8 pt-4">
                {stats.slice(0, 3).map((stat, i) => (
                  <div key={i} className="text-center">
                    <p className="text-2xl font-bold text-[#C4956A]">
                      {stat.value}
                      {stat.suffix}
                    </p>
                    <p className="text-xs text-[#1A1A2E]/50 mt-1">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <img
                  src={heroImages.main}
                  alt="Appartement rénové à Marseille"
                  className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#C4956A]/10 flex items-center justify-center">
                    <Key className="w-6 h-6 text-[#C4956A]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#1A1A2E]">
                      Clé en main
                    </p>
                    <p className="text-xs text-[#1A1A2E]/50">
                      De la recherche à la location
                    </p>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm font-bold text-[#1A1A2E]">
                      100%
                    </span>
                  </div>
                  <p className="text-xs text-[#1A1A2E]/50 mt-1">
                    Clients satisfaits
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A1A2E] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <AnimatedSection key={i}>
                <div className="text-center">
                  <p className="text-3xl lg:text-4xl font-bold text-[#C4956A]">
                    <CountUp end={stat.value} suffix={stat.suffix} />
                  </p>
                  <p className="text-sm text-white/60 mt-2">{stat.label}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Services / Notre Méthode */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
                Notre méthode
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A2E] mt-3">
                Un service conçu pour simplifier votre investissement
              </h2>
              <p className="text-[#1A1A2E]/60 mt-4">
                5 étapes clés pour un investissement locatif réussi, sans charge
                mentale.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
              const Icon = iconMap[service.icon];
              return (
                <AnimatedSection key={i}>
                  <div className="group relative p-8 bg-[#FAF9F6] rounded-2xl hover:bg-white hover:shadow-xl hover:shadow-[#C4956A]/5 transition-all duration-500 border border-transparent hover:border-[#E8DDD3]">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-4xl font-bold text-[#C4956A]/20">
                        {service.step}
                      </span>
                      <div className="w-12 h-12 rounded-xl bg-[#C4956A]/10 flex items-center justify-center group-hover:bg-[#C4956A] transition-colors duration-300">
                        {Icon && (
                          <Icon className="w-5 h-5 text-[#C4956A] group-hover:text-white transition-colors duration-300" />
                        )}
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-[#1A1A2E] mb-3">
                      {service.title}
                    </h3>
                    <p className="text-sm text-[#1A1A2E]/60 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </AnimatedSection>
              );
            })}
            {/* CTA Card */}
            <AnimatedSection>
              <Link
                to="/solution"
                className="group relative p-8 bg-[#C4956A] rounded-2xl flex flex-col justify-center items-center text-center hover:bg-[#B38459] transition-all duration-500 h-full"
              >
                <h3 className="text-xl font-semibold text-white mb-3">
                  Découvrir le processus complet
                </h3>
                <p className="text-white/70 text-sm mb-6">
                  Les 7 étapes détaillées de votre projet
                </p>
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                  <ArrowRight className="w-5 h-5 text-white" />
                </div>
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Why Marseille */}
      <section className="py-24 bg-[#FAF9F6] relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="space-y-6">
                <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
                  Pourquoi Marseille
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A2E]">
                  Investir à Marseille :{" "}
                  <span className="text-[#C4956A]">un choix stratégique</span>
                </h2>
                <p className="text-[#1A1A2E]/60 leading-relaxed">
                  Marseille offre un équilibre rare entre dynamique de
                  valorisation, profondeur de marché et accessibilité. Une
                  métropole en pleine transformation.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 pt-4">
                  {whyMarseille.slice(0, 4).map((item, i) => {
                    const Icon = iconMap[item.icon];
                    return (
                      <div
                        key={i}
                        className="flex items-start gap-3 p-4 bg-white rounded-xl"
                      >
                        <div className="w-10 h-10 rounded-lg bg-[#C4956A]/10 flex items-center justify-center flex-shrink-0">
                          {Icon && (
                            <Icon className="w-5 h-5 text-[#C4956A]" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#1A1A2E]">
                            {item.title}
                          </p>
                          <p className="text-xs text-[#1A1A2E]/50 mt-1 leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Link
                  to="/investir"
                  className="inline-flex items-center gap-2 text-[#C4956A] font-medium text-sm hover:gap-3 transition-all"
                >
                  En savoir plus sur Marseille
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>
            <AnimatedSection>
              <div className="relative">
                <img
                  src={marseilleImage}
                  alt="Marseille"
                  className="w-full h-[450px] object-cover rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A2E]/30 to-transparent rounded-2xl" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Realisations */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-12 gap-4">
              <div>
                <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
                  Nos réalisations
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A2E] mt-3">
                  Projets récents
                </h2>
              </div>
              <Link
                to="/realisations"
                className="inline-flex items-center gap-2 text-[#C4956A] font-medium text-sm hover:gap-3 transition-all"
              >
                Voir toutes les réalisations
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {realisations.slice(0, 6).map((project, i) => (
              <AnimatedSection key={project.id}>
                <div className="group relative overflow-hidden rounded-2xl bg-[#FAF9F6]">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-[#1A1A2E]">
                        {project.type}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-[#1A1A2E]">
                      {project.name}
                    </h3>
                    <p className="text-sm text-[#1A1A2E]/50 mt-1">
                      {project.location}
                    </p>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#E8DDD3]/50">
                      <span className="flex items-center gap-1 text-xs text-[#1A1A2E]/50">
                        <Maximize2 className="w-3.5 h-3.5" />
                        {project.surface}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-[#1A1A2E]/50">
                        <BedDouble className="w-3.5 h-3.5" />
                        {project.type}
                      </span>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#1A1A2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
                Témoignages
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mt-3">
                Ils nous ont fait confiance
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <AnimatedSection key={i}>
                <div className="p-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-[#C4956A]/30 transition-colors duration-300">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, j) => (
                      <Star
                        key={j}
                        className="w-4 h-4 text-yellow-400 fill-yellow-400"
                      />
                    ))}
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed mb-6">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#C4956A] flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {testimonial.initial}
                      </span>
                    </div>
                    <p className="text-white font-medium text-sm">
                      {testimonial.name}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Nos derniers articles */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
                Nos conseils
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A2E] mt-3">
                Nos derniers articles
              </h2>
              <p className="text-[#1A1A2E]/60 mt-4">
                {"Retrouvez nos analyses et conseils pour réussir votre investissement locatif à Marseille."}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.isArray(articles) ? articles.map((article) => (
              <AnimatedSection key={article.id}>
                <Link
                  to={`/ressources/${article.slug}`}
                  className="group block"
                >
                  <div className="relative h-52 overflow-hidden rounded-xl mb-4">
                    <img
                      src={article.image}
                      alt={article.titre}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-[#C4956A] text-white text-xs font-medium rounded-full">
                        {article.categorie}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#1A1A2E]/40 mb-2">
                    <Calendar className="w-3.5 h-3.5" />
                    {article.date}
                  </div>
                  <h3 className="text-lg font-semibold text-[#1A1A2E] group-hover:text-[#C4956A] transition-colors mb-2 leading-tight">
                    {article.titre}
                  </h3>
                  <p className="text-sm text-[#1A1A2E]/60 leading-relaxed">
                    {article.description}
                  </p>
                  <div className="flex items-center gap-1 mt-3 text-sm text-[#C4956A] font-medium">
                    Lire la suite
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </AnimatedSection>
            )) : null}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/ressources"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C4956A] text-white font-medium rounded-lg hover:bg-[#B38459] transition-all duration-300 hover:shadow-lg hover:shadow-[#C4956A]/20"
            >
              Voir tous les articles
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="bg-[#1A1A2E] rounded-3xl p-12 lg:p-16 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="space-y-4 text-center lg:text-left">
                <h2 className="text-3xl lg:text-4xl font-bold text-white">
                  Prêt à investir{" "}
                  <span className="text-[#C4956A]">sereinement</span> ?
                </h2>
                <p className="text-white/60 max-w-md">
                  Prenez rendez-vous dès maintenant avec notre équipe pour
                  discuter de votre projet d'investissement à Marseille.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C4956A] text-white font-medium rounded-lg hover:bg-[#B38459] transition-all duration-300"
                >
                  Prendre rendez-vous
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 transition-all duration-300"
                >
                  <Phone className="w-4 h-4" />
                  Être rappelé
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
